import { fetchMediaByParent, stripHtml } from "../services/wpPagesApi";

export function getProjectAcf(project) {
  const acf = project?.acf;
  if (!acf || Array.isArray(acf) || typeof acf !== "object") {
    return {};
  }
  return acf;
}

export function getProjectDisplayName(project, lang = "ar") {
  const acf = getProjectAcf(project);
  const useEnglish = String(lang || "").startsWith("en");
  const primary = String(useEnglish ? acf.english_name : acf.arabic_name || "").trim();
  const fallback = String(useEnglish ? acf.arabic_name : acf.english_name || "").trim();

  if (primary) return primary;
  if (fallback) return fallback;

  return stripHtml(project?.title?.rendered || "");
}

function normalizeMedia({ url, alt = "", width = null, height = null }) {
  if (!url) return null;
  return {
    url,
    alt: String(alt || "").trim(),
    width,
    height,
  };
}

function mediaFromEmbedded(project) {
  const media = project?._embedded?.["wp:featuredmedia"]?.[0];
  if (!media?.source_url || media.code) return null;

  return normalizeMedia({
    url: media.source_url,
    alt: media.alt_text || stripHtml(media.title?.rendered || ""),
    width: media.media_details?.width || media.width || null,
    height: media.media_details?.height || media.height || null,
  });
}

function mediaFromAcfValue(value, mediaMap) {
  if (typeof value === "number" && value > 0) {
    return normalizeMedia(mediaMap[value] || {});
  }

  if (!value || typeof value !== "object") return null;

  if (value.url) {
    return normalizeMedia({
      url: value.url,
      alt: value.alt || value.title || "",
      width: value.width || value.sizes?.large?.width || null,
      height: value.height || value.sizes?.large?.height || null,
    });
  }

  if (typeof value.ID === "number" && value.ID > 0) {
    return mediaFromAcfValue(value.ID, mediaMap);
  }

  return null;
}

function walkAcfMediaIds(value, ids) {
  if (typeof value === "number" && value > 0) {
    ids.add(value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => walkAcfMediaIds(item, ids));
    return;
  }

  if (value && typeof value === "object") {
    if (typeof value.ID === "number" && value.ID > 0) {
      ids.add(value.ID);
      return;
    }
    if (typeof value.image === "number" && value.image > 0) {
      ids.add(value.image);
    }
    Object.values(value).forEach((item) => walkAcfMediaIds(item, ids));
  }
}

export function collectProjectMediaIds(projects = []) {
  const ids = new Set();

  projects.forEach((project) => {
    const featuredMedia = project?.featured_media;
    const embedded = project?._embedded?.["wp:featuredmedia"]?.[0];

    if (typeof featuredMedia === "number" && featuredMedia > 0 && !embedded?.source_url) {
      ids.add(featuredMedia);
    }

    const acf = getProjectAcf(project);
    ["image", "project_image", "gallery", "project_gallery", "photos"].forEach((key) => {
      walkAcfMediaIds(acf[key], ids);
    });
  });

  return [...ids];
}

export function getProjectGalleryItems(project, mediaMap = {}, { lang = "ar" } = {}) {
  const title = getProjectDisplayName(project, lang);
  const items = [];
  const seenUrls = new Set();

  const addItem = (id, media) => {
    if (!media?.url || seenUrls.has(media.url)) return;
    seenUrls.add(media.url);
    items.push({
      id,
      title,
      ...media,
      alt: media.alt || title,
    });
  };

  addItem(`${project.id}-featured`, mediaFromEmbedded(project));

  const acf = getProjectAcf(project);
  ["image", "project_image"].forEach((key) => {
    addItem(`${project.id}-${key}`, mediaFromAcfValue(acf[key], mediaMap));
  });

  ["gallery", "project_gallery", "photos"].forEach((key) => {
    const rows = acf[key];
    if (!Array.isArray(rows)) return;

    rows.forEach((entry, index) => {
      const value = entry && typeof entry === "object" ? entry.image ?? entry : entry;
      addItem(`${project.id}-${key}-${index}`, mediaFromAcfValue(value, mediaMap));
    });
  });

  if (Array.isArray(project._attachments)) {
    project._attachments.forEach((media, index) => {
      addItem(`${project.id}-attachment-${index}`, normalizeMedia(media));
    });
  }

  return items;
}

export function getProjectsGalleryItems(projects = [], mediaMap = {}, options = {}) {
  return projects.flatMap((project) => getProjectGalleryItems(project, mediaMap, options));
}

export async function enrichProjectsWithAttachments(projects = [], mediaMap = {}, options = {}) {
  const enriched = projects.map((project) => ({ ...project, _attachments: [] }));
  const projectsNeedingAttachments = enriched.filter(
    (project) => getProjectGalleryItems(project, mediaMap, options).length === 0,
  );

  await Promise.all(
    projectsNeedingAttachments.map(async (project) => {
      const attachments = await fetchMediaByParent(project.id);
      project._attachments = attachments;
    }),
  );

  return enriched;
}
